<template>
    <div :class="{ 'input-style-tailwind': inputStyle === 'tailwind' }">
        <v-combobox
            :id="inputId"
            ref="field"
            v-model="selectedCountry"
        :items="countryOptions"
        :error-messages="errorMessages"
        :label="label"
        :disabled="disabled"
        :dense="dense"
        :clearable="clearable"
        :placeholder="placeholder"
        :persistent-placeholder="persistentPlaceholder"
        :filter="filterCountries"
        :search-input.sync="search"
        :outlined="outlined"
        :background-color="backgroundColor"
        :loading="loading"
        :prepend-inner-icon="prependInnerIcon"
        :required="required"
        :flat="flat"
        :hide-details="hideDetails"
        :class="className"
        item-text="name"
        item-value="code"
        @input="handleInput"
        @change="handleChange"
    >
        <template slot="selection" slot-scope="{ item }">
            <span v-if="typeof item === 'string'">{{ $utils.getCountryName(item) }}</span>
            <span v-else>{{ $utils.getCountryName(item.code) }}</span>
        </template>
        <template slot="item" slot-scope="{ item }">
            <span>{{ $utils.getCountryName(item.code) }}</span>
        </template>
    </v-combobox>
    </div>
</template>

<script>
export default {
    props: {
        inputId: String,
        value: {
            type: String,
            default: null,
        },
        errorMessages: {
            type: Array,
            default: () => [],
        },
        label: {
            type: String,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dense: {
            type: Boolean,
            default: false,
        },
        clearable: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: null,
        },
        persistentPlaceholder: {
            type: Boolean,
            default: false,
        },
        outlined: {
            type: Boolean,
            default: false,
        },
        backgroundColor: { 
            type: String,
            default: null,
        },
        className: { 
            type: String,
            default: null,
        },
        loading: { 
            type: Boolean,
            default: false,
        },
        prependInnerIcon: { 
            type: String,
            default: null,
        },
        required: { 
            type: Boolean,
            default: false,
        },
        flat: { 
            type: Boolean,
            default: false,
        },
        hideDetails: { 
            type: [Boolean, String],
            default: false,
        },
        /** 'vuetify' (default) or 'tailwind' – passed from parent (e.g. AddressForm) */
        inputStyle: {
            type: String,
            default: 'vuetify',
            validator: (v) => ['vuetify', 'tailwind'].includes(v),
        },
        fontSize: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            search: null,
            countries: [
                'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
                'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ',
                'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ',
                'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
                'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET',
                'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
                'GA', 'GB', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY',
                'HK', 'HM', 'HN', 'HR', 'HT', 'HU',
                'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT',
                'JE', 'JM', 'JO', 'JP',
                'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ',
                'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
                'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
                'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ',
                'OM',
                'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY',
                'QA',
                'RE', 'RO', 'RS', 'RU', 'RW',
                'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SX', 'SY', 'SZ',
                'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ',
                'UA', 'UG', 'UM', 'US', 'UY', 'UZ',
                'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU',
                'WF', 'WS',
                'YE', 'YT',
                'ZA', 'ZM', 'ZW',
            ],
        };
    },
    computed: {
        countryOptions() {
            const options = this.countries.map(code => ({
                code,
                name: this.$utils.getCountryName(code)
            })).sort((a, b) => a.name.localeCompare(b.name));
            
            // Mettre la France en premier si elle existe
            const franceIndex = options.findIndex(option => option.code === 'FR');
            if (franceIndex > 0) {
                const france = options.splice(franceIndex, 1)[0];
                options.unshift(france);
            }
            
            return options;
        },
        selectedCountry: {
            get() {
                // Si la valeur est un objet (depuis v-combobox), retourner le code
                if (typeof this.value === 'object' && this.value && this.value.code) {
                    return this.value.code;
                }
                return this.value;
            },
            set(value) {
                // Si v-combobox retourne un objet, extraire le code
                if (typeof value === 'object' && value && value.code) {
                    this.$emit('input', value.code);
                } else {
                    this.$emit('input', value);
                }
            },
        },
        fontSizeStyle() {
            if (!this.fontSize) {
                return {};
            }
            return {
                '--country-select-font-size': this.fontSize,
            };
        },
    },
    methods: {
        filterCountries(item, queryText, itemText) {
            if (!queryText) return true;
            const searchText = queryText.toLowerCase();
            const translatedName = this.$utils.getCountryName(item.code).toLowerCase();
            return translatedName.includes(searchText);
        },
        handleInput(value) {
            // Si v-combobox retourne un objet, extraire le code directement
            if (typeof value === 'object' && value && value.code) {
                this.$emit('input', value.code);
                return;
            }
            
            // Si l'utilisateur tape un nom de pays, on cherche le code correspondant
            if (typeof value === 'string' && value.length > 0) {
                const searchValue = value.toLowerCase();
                
                // Chercher par nom traduit
                const countryCode = this.countries.find(code => {
                    const translatedName = this.$utils.getCountryName(code).toLowerCase();
                    return translatedName === searchValue;
                });
                
                if (countryCode) {
                    this.$emit('input', countryCode);
                    return;
                }
            }
            this.$emit('input', value);
        },
        handleChange(value) {
            // Si v-combobox retourne un objet, extraire le code
            if (typeof value === 'object' && value && value.code) {
                this.$emit('change', value.code);
            } else {
                this.$emit('change', value);
            }
        },
        openMenu() {
            const ctrl = this.$refs.field;
            if (!ctrl) {
                return;
            }
            if (typeof ctrl.focus === "function") {
                ctrl.focus();
            }
            this.$nextTick(() => {
                if (typeof ctrl.activateMenu === "function") {
                    ctrl.activateMenu();
                } else if (Object.prototype.hasOwnProperty.call(ctrl, "isMenuActive")) {
                    ctrl.isMenuActive = true;
                }
            });
        },
    },
};
</script>

<style lang="scss" scoped>
/* Tailwind-style when inputStyle === 'tailwind' (e.g. from AddressForm). Match native rounded = 0.25rem. */
.input-style-tailwind ::v-deep .v-combobox {
    border-radius: 0.25rem !important;
    overflow: hidden !important;
}
.input-style-tailwind ::v-deep .v-combobox .v-input__slot {
    background-color: #f8fafc !important;
    border: 1px solid #f5f6f9 !important;
    border-radius: 0.25rem !important;
    min-height: 40px !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
}
.input-style-tailwind ::v-deep .v-combobox fieldset {
    border-radius: 0.25rem !important;
}
.input-style-tailwind ::v-deep .v-combobox.v-input--is-focused .v-input__slot {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
}
</style>